import React from 'react';
import { Info, Landmark, FileCheck, AlertTriangle, Book, DollarSign, Briefcase } from 'lucide-react';

/**
 * Компонент с информацией о НДС и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        О налоге на добавленную стоимость (НДС) в России
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Landmark className="w-5 h-5 text-indigo-500" />
          Что такое НДС?
        </h3>
        <p>
          <strong>Налог на добавленную стоимость (НДС)</strong> — это косвенный налог, который взимается с добавленной стоимости товаров и услуг на каждом этапе их производства и реализации. В России НДС является федеральным налогом и регулируется главой 21 Налогового кодекса РФ.
        </p>
        <p>
          НДС уплачивают организации и индивидуальные предприниматели при реализации товаров, работ и услуг на территории РФ, а также при импорте товаров. Конечным плательщиком НДС фактически является потребитель, поскольку налог включается в цену товаров или услуг.
        </p>

        <h3 className="flex items-center gap-2">
          <FileCheck className="w-5 h-5 text-indigo-500" />
          Ставки НДС в России
        </h3>
        <p>В России действуют следующие ставки НДС:</p>
        <ul>
          <li><strong>20%</strong> — основная ставка, применяемая по умолчанию к большинству товаров и услуг.</li>
          <li><strong>10%</strong> — льготная ставка, применяемая к определённым категориям товаров:
            <ul>
              <li>продовольственные товары (кроме деликатесов)</li>
              <li>товары для детей</li>
              <li>периодические печатные издания и книжная продукция, связанная с образованием, наукой и культурой</li>
              <li>медицинские товары</li>
            </ul>
          </li>
          <li><strong>0%</strong> — применяется для:
            <ul>
              <li>экспорта товаров</li>
              <li>международных перевозок</li>
              <li>услуг по перевозке пассажиров и багажа</li>
              <li>реализации товаров для официального использования международными организациями</li>
            </ul>
          </li>
          <li><strong>Освобождение от НДС</strong> — предусмотрено для определенных видов деятельности (ст. 149 НК РФ):
            <ul>
              <li>медицинские услуги</li>
              <li>образовательные услуги</li>
              <li>финансовые услуги</li>
              <li>услуги в сфере культуры и искусства</li>
              <li>и другие</li>
            </ul>
          </li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Расчетные ставки НДС</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Для выделения НДС из суммы, включающей налог, применяются расчетные ставки:
            <br />
            <strong>20/120</strong> (16,67%) — для товаров/услуг с 20% ставкой
            <br />
            <strong>10/110</strong> (9,09%) — для товаров/услуг с 10% ставкой
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-500" />
          Методы расчета НДС
        </h3>
        <p>
          Существует два основных метода расчета НДС:
        </p>
        <ol>
          <li>
            <strong>Начисление НДС (прямой расчет)</strong> — когда имеется сумма без НДС, и необходимо рассчитать сумму с НДС и сумму налога:
            <ul>
              <li>Сумма НДС = Сумма без НДС × Ставка НДС / 100</li>
              <li>Сумма с НДС = Сумма без НДС + Сумма НДС</li>
            </ul>
          </li>
          <li>
            <strong>Выделение НДС (обратный расчет)</strong> — когда известна общая сумма с НДС, и необходимо выделить налог:
            <ul>
              <li>Сумма НДС = Сумма с НДС × Расчетная ставка / 100</li>
              <li>Сумма без НДС = Сумма с НДС - Сумма НДС</li>
            </ul>
          </li>
        </ol>

        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 my-6">
          <h4 className="text-amber-700 dark:text-amber-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Особенности работы с НДС для бизнеса
          </h4>
          <ul className="text-amber-600 dark:text-amber-400 text-sm mb-0">
            <li>Плательщики НДС обязаны выставлять счета-фактуры при продаже товаров, работ, услуг</li>
            <li>НДС, уплаченный поставщикам (входящий НДС), может быть принят к вычету</li>
            <li>Необходимо вести книги покупок и продаж для учета НДС</li>
            <li>Декларация по НДС подается ежеквартально</li>
            <li>С 2015 года налогоплательщики обязаны подавать декларации по НДС в электронной форме</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-indigo-500" />
          Кто не является плательщиком НДС
        </h3>
        <p>
          В России некоторые категории налогоплательщиков не платят НДС:
        </p>
        <ul>
          <li>организации и ИП на упрощенной системе налогообложения (УСН)</li>
          <li>организации и ИП на патентной системе налогообложения (ПСН)</li>
          <li>организации и ИП, применяющие единый сельскохозяйственный налог (ЕСХН), при соблюдении определенных условий</li>
          <li>плательщики налога на профессиональный доход (самозанятые)</li>
          <li>организации и ИП, использующие систему налогообложения в виде единого налога на вмененный доход (ЕНВД) до 2021 года (сейчас отменена)</li>
        </ul>
        <p>
          Также организации и ИП могут получить освобождение от обязанностей плательщика НДС, если за последние три месяца их выручка не превысила 2 млн рублей (без учета НДС).
        </p>

        <h3 className="flex items-center gap-2">
          <Book className="w-5 h-5 text-indigo-500" />
          Документы по НДС
        </h3>
        <p>
          Основные документы, связанные с НДС:
        </p>
        <ul>
          <li><strong>Счет-фактура</strong> — документ, который выставляется продавцом покупателю при реализации товаров, работ, услуг. Является основанием для принятия НДС к вычету.</li>
          <li><strong>Универсальный передаточный документ (УПД)</strong> — объединяет в себе функции счета-фактуры и первичного документа.</li>
          <li><strong>Книга покупок</strong> — регистр для учета счетов-фактур, на основании которых НДС принимается к вычету.</li>
          <li><strong>Книга продаж</strong> — регистр для учета счетов-фактур, выставленных покупателям.</li>
          <li><strong>Декларация по НДС</strong> — документ, который подается в налоговую инспекцию ежеквартально.</li>
        </ul>

        <h3>Изменения в НДС с 2019 года</h3>
        <p>
          С 1 января 2019 года основная ставка НДС была повышена с 18% до 20%. Расчетная ставка, соответственно, изменилась с 18/118 на 20/120.
        </p>
        <p>
          Также с 2019 года введен порядок, по которому иностранные компании, оказывающие электронные услуги организациям и ИП в России, должны самостоятельно исчислять и уплачивать НДС в российский бюджет.
        </p>

        <p className="font-semibold mt-6">
          Наш калькулятор НДС поможет вам быстро и точно рассчитать налог для разных ставок и типов операций!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;